"use client";
import { isRecitationMeeting } from '@/app/lib/meetingTypes';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { GroupedCourse, ScheduledCourse } from '@/app/types/course';
import teachersData from '@/data/teachers_data.json';
import { openSectionsService } from '@/app/services/api';
import { CAMPUS_COLORS } from '@/app/types/colors';
import Modal from '@/app/Components/Modal';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import { normalizeRutgersBaseCourseId } from '@/app/lib/courseIdFormatter';

interface Professor {
  id: string;
  firstName: string;
  lastName: string;
  avgDifficulty: number;
  avgRating: number;
  department: string;
  numRatings: number;
  wouldTakeAgainPercent: number;
}

const typedTeachersData: Professor[] = teachersData as Professor[];

interface CourseListConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseListGroup: GroupedCourse | null;
  onSave: (quantity: number, allowedCourses: { [courseId: string]: string[] }) => void;
  initialCourseId?: string;
  initialSectionId?: string;
  completedCourseBaseIds?: Set<string>;
  /** Matches schedule term for openSectionsService (e.g. spring-2026). */
  openSectionsTermId?: string;
  /** Authoritative selected-term membership supplied by Scheduling's catalog. */
  catalogSections?: readonly Pick<ScheduledCourse, 'id' | 'baseId'>[];
  catalogReady?: boolean;
}

const EMPTY_COMPLETED_SET = new Set<string>();

export default function CourseListConfigurationModal({ 
  isOpen, 
  onClose, 
  courseListGroup, 
  onSave,
  initialCourseId,
  initialSectionId,
  completedCourseBaseIds,
  openSectionsTermId = 'fall-2026',
  catalogSections,
  catalogReady = true,
}: CourseListConfigurationModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [allowedCourses, setAllowedCourses] = useState<{ [courseId: string]: string[] }>({});
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [sortByCourse, setSortByCourse] = useState<'none' | 'rating' | 'name'>('none');
  const [filterCourseOpen, setFilterCourseOpen] = useState<boolean>(false);
  const completedCourses = completedCourseBaseIds ?? EMPTY_COMPLETED_SET;

  // Group sections by course (baseId)
  const coursesGrouped = useMemo(() => {
    if (!courseListGroup || !courseListGroup.sections) return new Map<string, ScheduledCourse[]>();
    
    const grouped = new Map<string, ScheduledCourse[]>();
    if (!catalogReady) return grouped;
    const currentKeys = catalogSections ? new Set(catalogSections.map(section => `${normalizeRutgersBaseCourseId(section.baseId)}|${section.id}`)) : null;
    courseListGroup.sections.forEach(section => {
      if (currentKeys && !currentKeys.has(`${normalizeRutgersBaseCourseId(section.baseId)}|${section.id}`)) return;
      const baseId = section.baseId;
      if (!grouped.has(baseId)) {
        grouped.set(baseId, []);
      }
      grouped.get(baseId)!.push(section);
    });
    return grouped;
  }, [courseListGroup, catalogSections, catalogReady]);

  useEffect(() => {
    if (courseListGroup && isOpen) {
      // Initialize with saved allowedCourses if they exist
      const savedAllowedCourses = courseListGroup.allowedCourses || {};
      // Initialize all courses with all sections selected if not configured
      const initialAllowedCourses: { [courseId: string]: string[] } = {};
      coursesGrouped.forEach((sections, courseId) => {
        if (savedAllowedCourses[courseId]) {
          initialAllowedCourses[courseId] = completedCourses.has(courseId) ? [] : savedAllowedCourses[courseId].filter(id => sections.some(section => section.id === id));
        } else {
          initialAllowedCourses[courseId] = completedCourses.has(courseId) ? [] : sections.map(s => s.id);
        }
      });
      
      setAllowedCourses(initialAllowedCourses);
      setQuantity(courseListGroup.quantity || 1);
    }
  }, [courseListGroup, isOpen, coursesGrouped, completedCourseBaseIds]);

  useEffect(() => {
    if (isOpen && initialCourseId) {
      setExpandedCourses((prev) => {
        const next = new Set(prev);
        next.add(initialCourseId);
        return next;
      });
    }
  }, [initialCourseId, isOpen]);

  // Auto-save when quantity or allowedCourses changes
  // Function to find professor data by name
  const findProfessorData = useCallback((professorName: string): Professor | null => {
    if (!professorName || professorName === 'TBA') return null;
    
    const cleanName = professorName.replace(/,/g, '').trim().toLowerCase();
    
    return typedTeachersData.find(prof => {
      const fullName = `${prof.firstName} ${prof.lastName}`.toLowerCase();
      const reverseName = `${prof.lastName} ${prof.firstName}`.toLowerCase();
      return fullName.includes(cleanName) || reverseName.includes(cleanName) || cleanName.includes(fullName);
    }) || null;
  }, []);

  // Helper function to get rating color
  const getRatingColor = (rating: number): string => {
    if (rating >= 4.0) return 'text-green-600 dark:text-green-400';
    if (rating >= 3.5) return 'text-green-500 dark:text-green-500';
    if (rating >= 3.0) return 'text-yellow-500 dark:text-yellow-500';
    if (rating >= 2.5) return 'text-orange-500 dark:text-orange-500';
    return 'text-red-500 dark:text-red-400';
  };

  // Get campus-based styling
  const getCampusStyle = (campus: string): string => {
    const normalizedCampus = campus?.trim() || 'Unknown';
    
    if (normalizedCampus.toLowerCase().includes('online') || 
        normalizedCampus.toLowerCase().includes('web')) {
      return CAMPUS_COLORS['Online'];
    }
    if (normalizedCampus.toLowerCase().includes('remote')) {
      return CAMPUS_COLORS['Remote'];
    }
    if (normalizedCampus.toLowerCase().includes('c/d') || 
        normalizedCampus.toLowerCase().includes('cook') ||
        normalizedCampus.toLowerCase().includes('douglass')) {
      return CAMPUS_COLORS['Cook/Douglass'];
    }
    if (normalizedCampus.toLowerCase().includes('busch')) {
      return CAMPUS_COLORS['Busch'];
    }
    if (normalizedCampus.toLowerCase().includes('college avenue') ||
        normalizedCampus.toLowerCase().includes('college ave')) {
      return CAMPUS_COLORS['College Ave'];
    }
    if (normalizedCampus.toLowerCase().includes('livingston')) {
      return CAMPUS_COLORS['Livingston'];
    }
    if (normalizedCampus === 'NB' || normalizedCampus.toLowerCase() === 'nb') {
      return CAMPUS_COLORS['NB'];
    }
    
    return CAMPUS_COLORS[normalizedCampus as keyof typeof CAMPUS_COLORS] || CAMPUS_COLORS['NB'];
  };

  // Check if a section is open
  const isSectionOpen = useCallback((sectionId: string): boolean => {
    return openSectionsService.isOpen(sectionId, openSectionsTermId);
  }, [openSectionsTermId]);

  const toggleCourseSection = (courseId: string, sectionId: string) => {
    if (completedCourses.has(courseId)) return;
    setAllowedCourses(prev => {
      const courseSections = prev[courseId] || [];
      return {
        ...prev,
        [courseId]: courseSections.includes(sectionId)
          ? courseSections.filter(id => id !== sectionId)
          : [...courseSections, sectionId]
      };
    });
  };

  const updateSectionSelection = (entries: { courseId: string; sections: ScheduledCourse[] }[], selected: boolean) => {
    setAllowedCourses(prev => {
      const next = { ...prev };
      for (const { courseId, sections } of entries) {
        if (completedCourses.has(courseId)) continue;
        const ids = new Set(sections.map(section => section.id));
        next[courseId] = selected
          ? [...new Set([...(prev[courseId] ?? []), ...ids])]
          : (prev[courseId] ?? []).filter(id => !ids.has(id));
      }
      return next;
    });
  };

  const selectAllSectionsForCourse = (courseId: string) => updateSectionSelection(processedCourses.filter(course => course.courseId === courseId), true);
  const deselectAllSectionsForCourse = (courseId: string) => updateSectionSelection(processedCourses.filter(course => course.courseId === courseId), false);

  const toggleCourseExpanded = (courseId: string) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    if (catalogReady) {
      const currentAllowed = Object.fromEntries([...coursesGrouped].map(([courseId, sections]) => [courseId,
        completedCourses.has(courseId) ? [] : (allowedCourses[courseId] ?? []).filter(id => sections.some(section => section.id === id)),
      ]));
      onSave(quantity, currentAllowed);
    }
    onClose();
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate average ratings for each course and process courses
  // Must be called before early return to maintain hook order
  const processedCourses = useMemo(() => {
    if (!courseListGroup || coursesGrouped.size === 0) return [];
    
    let courses = Array.from(coursesGrouped.entries()).map(([courseId, sections]) => {
      // Calculate average professor rating for this course
      let totalRating = 0;
      let ratingCount = 0;
      
      sections.forEach(section => {
        const instructorNames = section.instructors.map(instructor => 
          typeof instructor === 'string' ? instructor : instructor.name
        ).join(', ');
        const profData = findProfessorData(instructorNames);
        if (profData) {
          totalRating += profData.avgRating;
          ratingCount++;
        }
      });
      
      const avgRating = ratingCount > 0 ? totalRating / ratingCount : 0;
      const courseName = sections[0]?.name || formatBaseIdDisplay(sections[0]?.baseId);
      
      return { courseId, sections, avgRating, courseName };
    });
    
    // Filter for courses with open sections if enabled
    if (filterCourseOpen) {
      courses = courses.map(course => ({ ...course, sections: course.sections.filter(section => isSectionOpen(section.id)) }))
        .filter(course => course.sections.length > 0);
    }
    
    // Sort courses
    if (sortByCourse !== 'none') {
      courses.sort((a, b) => {
        switch (sortByCourse) {
          case 'rating':
            return b.avgRating - a.avgRating;
          case 'name':
            return a.courseName.localeCompare(b.courseName);
          default:
            return 0;
        }
      });
    }
    
    return courses;
  }, [coursesGrouped, filterCourseOpen, sortByCourse, isSectionOpen, courseListGroup, findProfessorData]);

  // Filtering/bulk section choices never change the requested course count.
  const maxQuantity = Math.max(quantity, coursesGrouped.size);
  const hasEligibleSections = catalogReady && processedCourses.some(course => !completedCourses.has(course.courseId) && course.sections.length > 0);

  if (!isOpen || !courseListGroup) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      closeOnOverlayClick={false}
      ariaLabel={`Configure Course List: ${courseListGroup.name}`}
    >
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                Configure Course List: {courseListGroup.name}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Select number of courses and which sections to allow for each course
              </p>
            </div>
            <button
              type="button"
              aria-label="Save and close course list configuration"
              onClick={handleSave}
              className="grid min-h-12 min-w-12 place-items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Quantity selector */}
          <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-700 rounded-control">
            <label htmlFor="course-list-quantity" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Number of Courses to Include
            </label>
            <select
              id="course-list-quantity"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
              className="block w-full rounded-control border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'course' : 'courses'}
                </option>
              ))}
            </select>
          </div>

          {/* Filter and sort controls - only show if more than 5 courses */}
          {coursesGrouped.size > 5 && (
            <div className="mb-4 flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-control">
              <div className="flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="h-4 w-4 text-slate-500" />
                <select
                  aria-label="Sort courses"
                  value={sortByCourse}
                  onChange={(e) => setSortByCourse(e.target.value as any)}
                  className="text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-2 py-1"
                >
                  <option value="none">Sort by...</option>
                  <option value="rating">Avg Rating (high to low)</option>
                  <option value="name">Course Name</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={filterCourseOpen}
                  onChange={(e) => setFilterCourseOpen(e.target.checked)}
                  className="rounded border-slate-300 dark:border-slate-600"
                />
                Open sections only
              </label>
            </div>
          )}

          <div role="group" aria-label="Bulk section selection" className="mb-4 flex flex-wrap items-center gap-2">
            <button type="button" disabled={!hasEligibleSections} onClick={() => updateSectionSelection(processedCourses, true)} className="min-h-12 rounded-control bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
              Select all
            </button>
            <button type="button" disabled={!hasEligibleSections} onClick={() => updateSectionSelection(processedCourses, false)} className="min-h-12 rounded-control bg-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
              Deselect all
            </button>
            <p className="basis-full text-xs text-slate-600 dark:text-slate-400">
              Applies to eligible sections in the filtered list, including collapsed courses. Hidden selections and the number of courses stay unchanged. Already taken courses are excluded.
            </p>
          </div>
          {processedCourses.length === 0 && (
            <p role="status" className="mb-4 text-sm text-slate-600 dark:text-slate-400">
              {catalogReady ? 'No courses match this semester and filter.' : 'Loading course sections for the selected semester.'}
            </p>
          )}

          {/* Courses with sections */}
          <div className="space-y-3">
            {processedCourses.map(({ courseId, sections, avgRating, courseName: processedCourseName }) => {
              const isExpanded = expandedCourses.has(courseId);
              const allowedSections = allowedCourses[courseId] || [];
              const selectedCount = sections.filter(section => allowedSections.includes(section.id)).length;
              const isCompletedCourse = completedCourses.has(courseId);

              return (
                <div
                  key={courseId}
                  className="border border-slate-300 dark:border-slate-600 rounded-control overflow-hidden"
                >
                  {/* Course header */}
                  <div className="w-full flex items-center justify-between p-4">
                    <button
                      onClick={() => toggleCourseExpanded(courseId)}
                      className="flex min-w-0 items-center space-x-3 flex-1 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors rounded p-2 -m-2"
                    >
                      {isExpanded ? (
                        <ChevronUpIcon className="h-5 w-5 text-slate-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-slate-500" />
                      )}
                      <div className="min-w-0 text-left">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {processedCourseName}
                          </h4>
                          {normalizeRutgersBaseCourseId(courseId) && (
                            <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{formatBaseIdDisplay(courseId)}</span>
                          )}
                          {isCompletedCourse && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200">
                              Already Taken
                            </span>
                          )}
                          {avgRating > 0 && (
                            <>
                              <span className={`text-sm font-bold ${getRatingColor(avgRating)}`}>
                                {avgRating.toFixed(1)}
                              </span>
                              <div className="flex items-center">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarSolidIcon
                                    key={star}
                                    className={`h-3 w-3 ${
                                      star <= Math.round(avgRating)
                                        ? 'text-yellow-400'
                                        : 'text-slate-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {selectedCount} of {sections.length} sections allowed
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        disabled={isCompletedCourse}
                        onClick={() => selectAllSectionsForCourse(courseId)}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        disabled={isCompletedCourse}
                        onClick={() => deselectAllSectionsForCourse(courseId)}
                        className="px-3 py-1 text-xs bg-slate-600 hover:bg-slate-700 text-white rounded transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                    {/* Sections list */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
                      {sections.map((section) => {
                        const isSelected = allowedSections.includes(section.id);
                        const instructorNames = section.instructors.map(instructor => 
                          typeof instructor === 'string' ? instructor : instructor.name
                        ).join(', ');
                        const professorData = findProfessorData(instructorNames);
                        const isOpen = isSectionOpen(section.id);
                        const campusStyle = getCampusStyle(section.campuses?.[0] ?? section.campus);
                        const campusDisplay = section.campuses && section.campuses.length > 0
                          ? section.campuses.join(', ')
                          : section.campus;
                        const meetingSummary = section.meetingTimes && section.meetingTimes.length > 0
                          ? section.meetingTimes.map((mt) => {
                              const dayLabel = mt.day && mt.day !== 'TBA' ? mt.day : 'TBA';
                              const startLabel = mt.startMinute ? formatTime(mt.startMinute) : 'TBA';
                              const endLabel = mt.endMinute ? formatTime(mt.endMinute) : 'TBA';
                              const locationLabel = mt.location && mt.location !== 'TBA' ? ` @ ${mt.location}` : '';
                              const campusLabel = mt.campus && mt.campus !== campusDisplay ? ` (${mt.campus})` : '';
                              return `${isRecitationMeeting(mt) ? 'Recitation · ' : ''}${dayLabel} ${startLabel}-${endLabel}${locationLabel}${campusLabel}`;
                            }).join(', ')
                          : 'TBA';
                        const isFocused = initialSectionId === section.id;

                        return (
                          <button
                            key={section.id}
                            disabled={isCompletedCourse}
                            aria-pressed={isSelected}
                            onClick={() => toggleCourseSection(courseId, section.id)}
                            className={`w-full text-left p-2.5 rounded-control transition-all ${
                              isSelected
                                ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                            } ${isFocused ? 'ring-2 ring-blue-400 dark:ring-blue-300' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Selection indicator */}
                              <div className="flex-shrink-0 pt-0.5">
                                {isSelected ? (
                                  <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                    <CheckIcon className="h-3 w-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 border-2 border-slate-400 rounded-full" />
                                )}
                              </div>

                              {/* Main content */}
                              <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                                {/* Left column */}
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className={`rounded px-2 py-0.5 text-xs font-medium text-white ${campusStyle}`}>
                                      {section.section}
                                    </div>
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      isOpen 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                                    }`}>
                                      {isOpen ? 'OPEN' : 'CLOSED'}
                                    </span>
                                  </div>
                                  <div className="text-xs font-medium text-slate-900 dark:text-white truncate">
                                    {instructorNames || 'TBA'}
                                    {professorData && (
                                      <span className="ml-2 inline-flex items-center space-x-1">
                                        <span className={`font-bold ${getRatingColor(professorData.avgRating)}`}>
                                          {professorData.avgRating.toFixed(1)}
                                        </span>
                                        <span className="flex items-center">
                                          {[1, 2, 3, 4, 5].map((star) => (
                                            <StarSolidIcon
                                              key={star}
                                              className={`h-3 w-3 ${
                                                star <= Math.round(professorData.avgRating) 
                                                  ? 'text-yellow-400' 
                                                  : 'text-slate-300'
                                              }`}
                                            />
                                          ))}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Right column */}
                                <div className="space-y-1">
                                  <div className="text-xs text-slate-900 dark:text-white font-medium mb-0.5 border-b border-slate-300 dark:border-slate-600 pb-0.5">
                                    {campusDisplay} • {section.credits} cr
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-0.5 border-b border-slate-300 dark:border-slate-600 pb-0.5">
                                    {meetingSummary}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Close button */}
          <div className="flex justify-end mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleSave}
              disabled={!catalogReady}
              className="min-h-12 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-control transition-colors"
            >
              Done
            </button>
          </div>
        </div>
    </Modal>
  );
}
